/** Inline error for a website address that isn't fully qualified (VPLAT 17). */
export const WEBSITE_URL_ERROR = "Enter your full website address, including https:// (e.g., https://www.yoursite.com).";

/**
 * Normalize the Website URL typed in the setup wizard: trim it and silently strip trailing
 * slashes (VPLAT 18 prefers that to an error). Returns undefined unless the result is a fully
 * qualified http(s) address with a dotted host (VPLAT 17). A path is allowed, since some sites
 * serve from a subfolder; a query, fragment or credentials are not, because page paths are
 * appended to it.
 */
export const normalizeWebsiteUrl = (input: string): string | undefined => {
	const trimmed = input.trim().replace(/\/+$/, "");
	// URL() drops a bare "?" or "#", so reject those characters before parsing.
	if (!/^https?:\/\//i.test(trimmed) || /[?#@\s]/.test(trimmed)) {
		return undefined;
	}
	let url: URL;
	try {
		url = new URL(trimmed);
	} catch{
		return undefined;
	}
	const host = url.hostname;
	if (!host.includes(".") || host.startsWith(".") || host.endsWith(".") || host.includes("..")) {
		return undefined;
	}
	return trimmed;
};

/** Join the site's base URL and a page path without doubling or dropping the slash. */
export const pageUrl = (baseUrl: string, path: string | undefined) => {
	const base = baseUrl.replace(/\/+$/, "");
	const cleanPath = (path || "").trim();
	if (!cleanPath) {
		return base;
	}
	return `${base}/${cleanPath.replace(/^\/+/, "")}`;
};

/**
 * The host alone, which is how the Website block shows the site the plugin registered from
 * (MAX-4506): "https://www.erickrealty.com" reads "www.erickrealty.com". Undefined when there's
 * nothing to show, so the row can be left out. Anything but a string counts as nothing: the value
 * comes straight off user.json, and a number or object there would otherwise throw during render.
 */
export const displayHost = (url: string | undefined): string | undefined => {
	if (typeof url !== "string" || !url.trim()) {
		return undefined;
	}
	try {
		return new URL(url.trim()).host || undefined;
	} catch{
		return undefined;
	}
};
